import { autoinject, bindable, TaskQueue } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { AuthService } from 'app/um/services/auth-service';
import { AuthContext } from 'app/um/lib/auth-context';
import { guid } from 'app/shared/lib/guid';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IExtIqcSettings } from 'app/ext/iqc/ext-iqc';
import { checkForContainingDates, checkForOverlappingDates } from 'app/um/lib/utilities';
import { DateValue } from 'app/shared/lib/date-time';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { debounce } from 'app/shared/lib/decorators';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { CustomService } from 'app/um/components/auth/service-grid';
import * as UM from 'app/um/models/essette-api';

export type HasReviewsFlag = { hasReviews?: boolean };
type AuthServiceWithLocalState = ExtendLocalState<CustomService & HasReviewsFlag, { isExpanded?: boolean; }>;

const expandedServicesCache = new Map<string, Array<AuthServiceWithLocalState>>();

@autoinject()
export class IqcSummaryServicesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    protected auth?: UM.Auth;

    @bindable
    protected isDental = false;

    protected expandedCacheKey = 'iqc-summary-services-grid';

    protected settings: IExtIqcSettings = {
        showOpenReviewSummaryPdf: true,
        showOpenBookView: false,
        allowReviewCopy: false,
        useLatest: true
    };

    protected detailSettings: IExtIqcSettings = {
        showOpenReviewSummaryPdf: true,
        showOpenBookView: false,
        allowReviewCopy: false,
        useLatest: false
    };

    protected services: AuthServiceWithLocalState[] = [];
    protected resultCount = 0;
    protected sorting = new Sorting();
    protected defaultSorting = new Sorting();
    protected pageSize = 25;
    public allExpanded = true;
    public allCollapsed = false;

    protected servicesDetails: Record<string, boolean> = {};

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<AuthServiceWithLocalState[]>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected context: AuthContext,
        protected service: AuthService,
        private readonly queue: TaskQueue,
        ...rest: any[]) {
        super(...rest);

        this.defaultSorting.entries = [
            {
                reference: 'isPrimary',
                direction: SortDirection.Descending
            },
            {
                reference: 'definition.code',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        if(this.env.application.code === 'Portal.Providers') {
            this.settings.useDynamicSso = this.detailSettings.useDynamicSso = true;
            this.settings.useLatest = false;
        }
        if(this.isDental) this.expandedCacheKey = 'iqc-summary-dental-services-grid';
        this.sorting.add({
            reference: 'isPrimary',
            direction: SortDirection.Descending
        });
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadServices(false);
    }

    protected updateCollapsed(event: ExpandEvent<AuthServiceWithLocalState>): void {
        const item = event.detail.item;

        if(!item?.id) return;
        const foundService = this.services.find(s => s.id === item.id)
        if(foundService?._state) {
            foundService._state.isExpanded = !foundService._state.isExpanded;
        }

        this.allCollapsed = this.services.every(s => !s._state?.isExpanded);
        this.allExpanded = this.services.every(s => s._state?.isExpanded);

        this.servicesDetails[item.id!] = true;
    }

    @debounce()
    public expandAll(): void {
        this.servicesDetails = {};
        this.services.forEach((s: AuthServiceWithLocalState) => {
            if(s.hasReviews) this.servicesDetails[s.id!] = true;
        });
        this.services.forEach((s: AuthServiceWithLocalState) => {
            s._state = { isExpanded: true };
        });
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedServices = expandedServicesCache.get(this.expandedCacheKey);
            if(expandedServices !== undefined) {
                this.$table.restoreExpanded(expandedServices);
            }
        });
        this.allExpanded = true;
        this.allCollapsed = false;
    }

    public collapseAll(): void {
        this.servicesDetails = {};
        this.services.forEach((s: AuthServiceWithLocalState) => {
            s._state = { isExpanded: false };
        })
        if(this.$table) this.$table.closeAll();
        this.allExpanded = false;
        this.allCollapsed = true;
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadServices();
    }

    protected hasServiceDateConflict(service: UM.AuthService): boolean {
        if(!service) return false;

        const conflictingDates = (this.services?.filter((s: UM.AuthService) => {
            if(s.id === service.id && s.sequence !== service.sequence) {
                if(s && service) {
                    if(s.status?.determination?.status?.determination?.result) {
                        //if determination is denied or void, we don't care about dates for that item
                        switch(s.status?.determination?.status?.determination?.result) {
                            case 'other':
                            case 'denied':
                                return false;
                        }
                    }
                    return checkForOverlappingDates(s, service);
                }
            }
            return false;
        }));

        if(conflictingDates) {
            return conflictingDates.length > 0;
        }

        return false;
    }

    protected hasInvalidServiceDate(service: UM.AuthService): boolean {
        if(!service) return false;

        switch(service.status?.determination?.status?.determination?.result) {
            case 'other':
            case 'denied':
                return false;
        }

        return !checkForContainingDates(service.definition?.dateRange, service);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadServices(expandDetails: boolean = true): Promise<void> {
        if(!this.auth?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting.entries.length > 1 ? this.sorting : this.defaultSorting
        };

        const includes = [
            'definition',
            'flags',
            'medicalReview',
            'metadata.activeChanged.byUser',
            'modifiers',
            'status',
            'status.determination.status',
            'status.reason',
            'status.reasons',
            'status.transitions',
            'status.transitions.determination.status',
            'status.transitions.reasons',
            'metadata.created.byUser'
        ];

        if(this.isDental) includes.push('bodySite', 'bodySubSite');

        const fields = [
            'dateRange.*',
            'definition.code',
            'definition.description',
            'medicalReview.*',
            'status.description',
            'status.determination.*',
            'status.flag.*',
            'status.reasons.description',
            'status.transitions.description',
            'status.transitions.determination.*',
            'status.transitions.reasons.description',
            'authorizedDate',
            'correspondenceRulesTimestamp',
            'dueDateTime',
            'expirationDate',
            'flag.*',
            'flags.*',
            'isPrimary',
            'metadata.activeChanged.byUser.name.full',
            'metadata.activeChanged.deactivatedReason',
            'metadata.activeChanged.timestamp',
            'metadata.modified.timestamp',
            'modifiers.description',
            'quantity.approved',
            'quantity.requested',
            'sequence',
            'sequenceFlag'
        ];

        if(this.isDental) {
            fields.push('bodySite.description', 'bodySubSite.description');
        } else {
            fields.push('charge.approved', 'charge.requested');
        }

        if(this.env.application.code === 'Portal.Providers') {
            includes.push('medicalReview.history.medicalReviews');
            fields.push('medicalReview.history.medicalReviews.*');
        }

        try {
            this.state.beginLoad();

            const response = await this.service.loadServicesByAuth(this.auth.id, options, includes, { isDental: this.isDental }, fields);
            this.services = response.results;
            this.services.forEach(s => {
                s.hasReviews = !!s.medicalReview;
                // if in portal and medical review has been overwritten, retrieve appropriate external identifier from history
                if(this.env.application.code === 'Portal.Providers' && s.medicalReview?.identifier) {
                    const medicalReviews = s.medicalReview.history?.medicalReviews;
                    const foundPortalMedicalReview = medicalReviews?.find(review => review.createdInPortal);
                    if(foundPortalMedicalReview) s.medicalReview.identifier.external = foundPortalMedicalReview.identifier?.external;
                    else s.hasReviews = false;
                }
            });

            this.resultCount = response.count || 0;
            if(this.services.some(s => s.hasReviews) && expandDetails) {
                this.expandAll();
                expandedServicesCache.set(this.expandedCacheKey, this.services.filter(s => s.hasReviews));
            }

            //add duplicate auths for use with tooltip
            await this.loadDuplicateAuthsOntoServices();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadDuplicateAuthsOntoServices(): Promise<void> {
        if(!this.auth?.id || !this.auth.member?.plan?.id || !this.services.length) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes: string[] = ['auth', 'auth.status'];

        let filteredServices: UM.AuthService[] = [];
        try {
            this.state.beginLoad();
            const response = await this.service.loadFilteredAuthServices(this.auth.id, this.auth.member.plan.id, this.services, options, includes);
            filteredServices = response.results;
        } catch(error) {
            throw error;
        } finally {
            // only include auths started within the past year
            const date = new DateValue(this.auth?.serviceDate?.dateRange?.start! || this.auth?.initialDateOfService!);
            if(!date) return;
            const lookBackDate = date.add(-365, 'days');
            this.services.forEach(s => {
                s.auths = [];
                filteredServices.forEach(fs => {
                    if(fs.definition?.id === s.definition?.id && fs.auth && s.auths && s.auths.findIndex(a => a.id === fs.auth?.id) === -1) s.auths.push(fs.auth);
                });
                s.auths = s.auths.filter(a => {
                    return (a.serviceDate?.dateRange?.start! || a.initialDateOfService!)
                        && (lookBackDate.isBefore(new DateValue(a.serviceDate?.dateRange?.start! || a.initialDateOfService!)))
                });
            });
            this.state.endLoad();
        }
    }
}
